import i18next from "i18next";
import { useEffect, useState } from "react";
import { z } from "zod";
import { ProfileService } from "../../../services/profile.service";
import { AddressService } from "../../../services/address.service";
import { BusinessService } from "../../../services/business.service";
import { IBusinessResponse } from "../../../types/business";
import { UploadService } from "../../../services/upload.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import showErrorToast, {
  ErrorToastConfig,
  showSuccessToast,
  SuccessToastConfig,
} from "../../../components/Error";
import { IAddressResponse, IProfileResponse } from "../../../types/profile";

const generalSchema = z.object({
  fullName: z.string().min(1).max(100),
  mobile: z
    .string()
    .min(
      1,
      i18next.t("commons.error.form.required", {
        label: i18next.t("landing.signup.phoneNumber"),
      })
    )
    .max(
      100,
      i18next.t("commons.error.form.required", {
        label: i18next.t("landing.signup.phoneNumber"),
      })
    )
    .optional(),
  email: z
    .string()
    .email({
      message: i18next.t("commons.error.form.required", {
        label: i18next.t("landing.signup.email", {
          optional: "",
        }),
      }),
    })
    .or(z.literal("")),
  address: z.object({
    title: z.string().min(3),
    fullName: z.string().min(5),
    address: z.string().min(10),
    address2: z.string().optional().nullable(),
    countryCode: z.string().min(2),
    postalCode: z.string().optional(),
    province: z.string().min(5),
    phoneNumber: z.string().optional(),
    phoneNumberCountryCode: z.coerce
      .number()
      .transform((num) => num.toString())
      .refine((str) => str.length >= 1, {
        message: "Phone number country code invalid",
      })
      .optional(),
    defaultAddress: z.boolean().default(true),
  }),
  companyName: z.string().min(3),
  companyNumber: z.string().optional(),
  companyNumberCountryCode: z
    .string()
    .optional()
    .transform((arg, ctx) => {
      if (arg) {
        if (!new RegExp(/^[0-9]+$/).test(arg)) {
          ctx.addIssue({
            code: "custom",
            message: "Should be numbers",
          });
        }
      }
      return arg;
    }),
  companyWebsite: z.string().optional(),
  companyCountry: z.string(),
  companyProvince: z.string(),
  companyAddress: z.string(),
  companyAddress2: z.string().optional().nullable(),
  companyPostalCode: z
    .string()
    .optional()
    .transform((arg, ctx) => {
      if (arg) {
        if (!new RegExp(/^[0-9]+$/).test(arg)) {
          ctx.addIssue({
            code: "custom",
            message: "Should be numbers",
          });
        }
      }
      return arg;
    }),
});

const addressFieldMap: {
  businessField: keyof IBusinessResponse;
  addressField: keyof IAddressResponse;
}[] = [
  { businessField: "companyCountry", addressField: "countryCode" },
  { businessField: "companyNumber", addressField: "phoneNumber" },
  {
    businessField: "companyNumberCountryCode",
    addressField: "phoneNumberCountryCode",
  },
  { businessField: "companyAddress", addressField: "address" },
  { businessField: "companyAddress2", addressField: "address2" },
  { businessField: "companyProvince", addressField: "province" },
  { businessField: "companyPostalCode", addressField: "postalCode" },
];

const useContainer = () => {
  //#region local states
  const { businessId } = useParams();

  const imgExistanceCheckQuery = useQuery({
    queryKey: ["business-profile-image", businessId],
    queryFn: () => UploadService.checkIfImageExists(businessId!),
    enabled: !!businessId,
    retry: false,
    staleTime: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<IProfileResponse | null>(null);
  const [displayPicture, setDisplayPicture] = useState<string | null>(null);
  const [address, setAddress] = useState<IAddressResponse>();
  const [business, setBusiness] = useState<IBusinessResponse>();
  const [companyIcon, setCompanyIcon] = useState<File | null>(null);
  const [uploadedProfilePicture, setUploadedProfilePicture] =
    useState<File | null>(null);
  const [uploadedCompanyPicture, setUploadedCompanyPicture] =
    useState<File | null>(null);

  //#endregion
  //#region ----- life cycle methods

  const updateProfileMutation = useMutation({
    mutationFn: ProfileService.patchProfile,
    onSuccess: (response, payload) => {
      console.log("RESPONSE", response);
      if (!profile || !business) {
        setIsLoading(false);
        return;
      }

      if (uploadedProfilePicture) {
        handleUploadAccountPicture(
          uploadedProfilePicture,
          payload.id,
          "profile"
        );
      }

      if (uploadedCompanyPicture) {
        handleUploadAccountPicture(uploadedCompanyPicture, business.id);
      }

      setIsLoading(false);
      toast(
        i18next.t("profile.editProfile.success.message"),
        SuccessToastConfig
      );

      setTimeout(() => {
        window.location.href = "/client/profile";
      }, 1000);
    },
    onError: (error: any) => {
      toast(error.message, ErrorToastConfig);
    },
  });

  /**
   * Fetches the profile of the logged-in user.
   * If one is found, updates the local state with it.
   */
  async function getProfile() {
    const profileData = await ProfileService.getProfile();
    if (profileData) {
      setProfile(profileData);
      setDisplayPicture(
        import.meta.env.VITE_SPACES_HOST + "/profile/" + profileData.id
      );
    }
  }

  /**
   * Fetches the default address associated with the logged-in user.
   * If one is found, updates the local state with it.
   */
  async function getDefaultAddress() {
    const defaultAddress = await AddressService.getDefaultAddressByAccount();
    if (defaultAddress) {
      setAddress(defaultAddress);
    }
  }

  useEffect(() => {
    let mounted = true;
    if (!businessId) {
      return;
    }

    const fetchImage = async () => {
      if (!imgExistanceCheckQuery.data || !business?.id) {
        if (mounted) {
          setCompanyIcon(null);
        }
        return;
      }

      try {
        const file = await UploadService.urlToFile(
          imgExistanceCheckQuery.data,
          businessId + Date.now()
        );

        if (mounted) {
          setCompanyIcon(file);
        }
      } catch (error) {
        console.error("Failed to fetch image:", error);
        if (mounted) {
          setCompanyIcon(null);
        }
      }
    };

    fetchImage();

    return () => {
      mounted = false;
    };
  }, [imgExistanceCheckQuery.data, businessId]);

  /**
   * Fetches the business associated with the given business ID.
   * If one is found, updates the local state with it.
   * @param businessId The ID of the business to fetch.
   */
  async function getBusiness(businessId: string) {
    const businessData = await BusinessService.getBusinessById(businessId);
    if (businessData) {
      setBusiness(businessData);
    }
  }

  //#endregion
  //#region ----- handler methods

  /**
   * Handles the upload of a new profile picture.
   * @param file The file to be uploaded.
   */
  const handleUploadAccountPicture = (
    file: File,
    id: string,
    folder?: string
  ) => {
    if (!profile) return;

    setIsLoading(true);
    UploadService.upload(file, id, folder)
      .then((res) => {
        console.log(res);
        toast(
          showSuccessToast("Picture uploaded successfully"),
          SuccessToastConfig
        );
      })
      .catch((err) => {
        toast(showErrorToast(err), ErrorToastConfig);
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Maps the address to the company address fields if the company address fields are not populated.
   * @param {object} business the business object
   * @param {object} address the address object
   * @returns {object} the merged object with company address fields
   */
  const handleMapAddressToCompanyAddress = (business?: any, address?: any) => {
    const fields = {
      companyCountry: ["companyCountry", "countryCode"],
      companyNumber: ["companyNumber", "phoneNumber"],
      companyNumberCountryCode: [
        "companyNumberCountryCode",
        "phoneNumberCountryCode",
      ],
      companyAddress: ["companyAddress", "address"],
      companyAddress2: ["companyAddress2", "address2"],
      companyProvince: ["companyProvince", "province"],
      companyPostalCode: ["companyPostalCode", "postalCode"],
    };

    const result: any = {};
    for (const [key, [businessField, addressField]] of Object.entries(fields)) {
      result[key] = business?.[businessField] || address?.[addressField];
    }

    return result;
  };

  /**
   * Compares the business fields with the address fields based on the predefined
   * mapping (addressFieldMap) and returns true if any of the fields do not match.
   * @param {IBusinessResponse} business the business information
   * @param {IAddressResponse} address the address information
   * @returns {boolean} whether the fields do not match
   */
  const handleCompareAddresses = (
    business?: Partial<IBusinessResponse>,
    address?: Partial<IAddressResponse>
  ): boolean => {
    return addressFieldMap.some(({ businessField, addressField }) => {
      const businessValue = business?.[businessField];
      const addressValue = address?.[addressField];

      return (
        businessValue == null ||
        businessValue === "" ||
        businessValue !== addressValue
      );
    });
  };

  //#endregion

  return {
    isLoading,
    setIsLoading,
    profile,
    setProfile,
    displayPicture,
    setDisplayPicture,
    address,
    setAddress,
    business,
    setBusiness,
    companyIcon,
    setCompanyIcon,
    uploadedProfilePicture,
    setUploadedProfilePicture,
    uploadedCompanyPicture,
    setUploadedCompanyPicture,
    generalSchema,
    getProfile,
    getDefaultAddress,
    getBusiness,
    handleMapAddressToCompanyAddress,
    handleCompareAddresses,
    handleUploadAccountPicture,
    updateProfileMutation,
  };
};

export default useContainer;
